/**
 * The Calendar section's two reads (`docs/plans/macro.md`, mock
 * `docs/mockups/macro-v2.html` section 03): the earnings each day, ranked for
 * the caller, and the economic releases.
 *
 * Both are **admin only** on the server until the owner has reviewed them; the
 * section host refuses a non-admin before either is called.
 *
 * - **Earnings** — `GET /macro/earnings` on the chart tier (branch
 *   `macro/earnings-calendar`, `services/qv-data/app/routers/earnings_calendar.py`).
 *   Asked with `perDay=all`: the server ranks (best source, then market cap) and
 *   this client slices to the section's per-day count, so "+N more" and a change
 *   of Per day are a re-slice rather than a refetch.
 * - **Economic** — `GET /macro/calendar/econ?range=week|month&date=` on the
 *   same server (branch `macro/eco-calendar`, `services/qv-data/app/routers/econcal.py`,
 *   shape `econcal/read.py` `view`). Asked with every release (`keyOnly` off):
 *   ★ Key / All is a client filter. 503 until the `econcal` artifact is on the
 *   machine, and the section says so.
 *
 * A failure throws and the section says so; nothing here retries.
 */
import { authHeaders } from "../lib/session";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(
  /\/$/,
  "",
);

/** False with no server configured: the section shows both reads as unavailable. */
export const earningsConfigured = BASE !== "";
export const econConfigured = BASE !== "";

/** The server's spelling of the sources, in rank order (`earnings_cal/rank.py` `SOURCES`). */
export type EarningsSourceWire = "journal" | "lists" | "group" | "topcap";

/** One reporter, as `rank._row` writes it. */
export interface EarningsRow {
  symbol: string;
  name: string | null;
  /** Dollars. */
  marketCap: number | null;
  /** 1 = the largest company; null when not in the top-cap set asked for. */
  capRank: number | null;
  logoUrl: string;
  /** The sources that put it here, best first; `find` alone when only Find did (`include`). */
  sources: (EarningsSourceWire | "find")[];
  /** Before the open, after the close, or not known. */
  time: "bmo" | "amc" | null;
  /** `fmp` from the vendor; `slot` from the symbol's usual time, which is a guess. */
  timeSource: "fmp" | "slot" | null;
  fiscalPeriod: string | null;
  periodEnding: string | null;
  epsEstimated: number | null;
  epsActual: number | null;
  lastQuarterEps: number | null;
  beats: number | null;
  beatsOf: number | null;
  /** EPS surprise as a fraction, oldest first, at most the last eight. */
  surprises: number[];
  /**
   * FMP's sector ("Technology"), for Group: Sector; null when unknown. A server
   * from before the field sends none, read as null (`fetchEarnings`).
   */
  sector: string | null;
}

export interface EarningsDay {
  date: string;
  /** Every matched reporter, ranked (asked with `perDay=all`). */
  rows: EarningsRow[];
  /** Every reporter that day, matched or not. */
  total: number;
  matched: number;
  hidden: number;
  matchedHidden: number;
}

export interface EarningsPayload {
  market: string;
  from: string;
  to: string;
  sources: EarningsSourceWire[];
  cap: number;
  perDay: number | null;
  /** False when the calendar artifact is missing on the server — not the same as "nobody reports". */
  available: boolean;
  days: EarningsDay[];
}

export interface EarningsQuery {
  from: string;
  to: string;
  sources: readonly EarningsSourceWire[];
  cap: number;
  /** Find's symbol: drawn even when no switched-on source holds it. */
  include?: string;
}

/** `GET /macro/earnings/find` — one symbol's next report, whatever the filters. */
export interface EarningsFind {
  symbol: string;
  name: string | null;
  /** 1 = the largest; null when not a ranked stock. */
  capRank: number | null;
  /** The switched-on sources that hold it; empty means the filters hide it. */
  sources: EarningsSourceWire[];
  report: {
    date: string;
    time: "bmo" | "amc" | null;
    timeSource: "fmp" | "slot" | null;
    epsEstimated: number | null;
    confirmed: boolean | null;
  } | null;
  /** The last day searched. */
  lookedTo: string;
}

export async function findEarnings(q: { symbol: string; from: string; sources: readonly EarningsSourceWire[]; cap: number }): Promise<EarningsFind> {
  if (!earningsConfigured) throw new Error("No data server configured");
  const params = new URLSearchParams({ symbol: q.symbol, from: q.from, cap: String(q.cap) });
  if (q.sources.length) params.set("sources", q.sources.join(","));
  return get<EarningsFind>(`/macro/earnings/find?${params}`, "Find");
}

async function get<T>(path: string, what: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((b) => b?.detail)
      .catch(() => undefined);
    throw new Error(typeof detail === "string" ? detail : `${what}: ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function fetchEarnings(q: EarningsQuery): Promise<EarningsPayload> {
  if (!earningsConfigured) throw new Error("No data server configured");
  const params = new URLSearchParams({
    from: q.from,
    to: q.to,
    cap: String(q.cap),
    perDay: "all",
  });
  // An empty `sources` means "all four" to the server; none switched on is
  // answered here instead, with nothing.
  if (q.include) params.set("include", q.include);
  if (q.sources.length === 0) {
    return { market: "US", from: q.from, to: q.to, sources: [], cap: q.cap, perDay: null, available: true, days: [] };
  }
  params.set("sources", q.sources.join(","));
  const p = await get<EarningsPayload>(`/macro/earnings?${params}`, "Earnings calendar");
  return { ...p, days: p.days.map((d) => ({ ...d, rows: d.rows.map((r) => ({ ...r, sector: r.sector ?? null })) })) };
}

/** One economic release. Times are Eastern, the vendor's clock converted on the server. */
export interface EconEvent {
  /** Stable within a response; the row's open/closed state keys on it. */
  id: string;
  /** The ET date. */
  date: string;
  /** `HH:MM`, 24-hour, Eastern; null = to be announced. */
  time: string | null;
  /** The report, its period stripped ("Initial Jobless Claims"). */
  name: string;
  /** The period the number is for ("Oct/24", "Q3"); null for a meeting. */
  period: string | null;
  forecast: string | null;
  previous: string | null;
  /** Null until released. */
  actual: string | null;
  /** ★ — a market-moving release (the server's `params.KEY_EVENTS`). */
  key: boolean;
  /**
   * Past released actuals of this same report (country + name), oldest first,
   * this one last when it is out. Only real releases — taken from the rows the
   * response holds (`withHistory`); absent when fewer than two are known.
   */
  history?: number[];
}

export interface EconPayload {
  from: string;
  to: string;
  events: EconEvent[];
}

/** One event as `econcal/read.py` `_event` writes it. */
export interface EconEventWire {
  id: string;
  timeEt: string | null;
  timeUtc: string;
  sourceTime: string | null;
  country: string;
  name: string;
  event: string;
  period: string | null;
  actual: number | null;
  forecast: number | null;
  previous: number | null;
  change: number | null;
  changePct: number | null;
  impact: string | null;
  unit: string | null;
  currency: string | null;
  key: boolean;
  keyLabel: string | null;
  released: boolean;
}

export interface EconDayWire {
  date: string;
  weekday: string;
  weekend: boolean;
  weekOf: string;
  inRange: boolean;
  total: number;
  keyCount: number;
  events: EconEventWire[];
}

/** `GET /macro/calendar/econ`, as `econcal/read.py` `view` returns it. */
export interface EconPayloadWire {
  range: "week" | "month";
  anchor: string;
  from: string;
  to: string;
  shownFrom: string;
  shownTo: string;
  tz: string;
  keyOnly: boolean;
  builtAt: string | null;
  days: EconDayWire[];
}

/** `8:30 AM` -> `08:30`; anything else (absent, unparsed) -> null, drawn as TBA. */
export function to24h(t: string | null): string | null {
  const m = t ? /^(\d{1,2}):(\d{2}) (AM|PM)$/.exec(t) : null;
  if (!m) return null;
  const h = (Number(m[1]) % 12) + (m[3] === "PM" ? 12 : 0);
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/** A value with the vendor's unit: `0.3%`, `194K`, `-132.6B`; `Points` and none add nothing. */
export function formatEconValue(v: number | null, unit: string | null): string | null {
  if (v == null || !Number.isFinite(v)) return null;
  const n = String(Number(v.toFixed(3)));
  const u = unit && ["%", "K", "M", "B", "T"].includes(unit) ? unit : "";
  return `${n}${u}`;
}

/**
 * Each event's `history`: the released actuals of the same report (country +
 * name) at or before it, from these rows only. Nothing is invented — a report
 * seen once has none.
 */
export function withHistory(rows: readonly { ev: EconEvent; wire: EconEventWire }[]): EconEvent[] {
  const series = new Map<string, { at: string; actual: number }[]>();
  for (const { wire } of rows) {
    if (wire.actual == null) continue;
    const k = `${wire.country}|${wire.name}`;
    const list = series.get(k) ?? [];
    list.push({ at: wire.timeUtc, actual: wire.actual });
    series.set(k, list);
  }
  for (const list of series.values()) list.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  return rows.map(({ ev, wire }) => {
    const past = (series.get(`${wire.country}|${wire.name}`) ?? []).filter((p) => p.at <= wire.timeUtc);
    return past.length >= 2 ? { ...ev, history: past.map((p) => p.actual) } : ev;
  });
}

/** The wire payload onto the section's rows. */
export function mapEconPayload(p: EconPayloadWire): EconPayload {
  const rows = p.days.flatMap((d) =>
    d.events.map((w) => ({
      wire: w,
      ev: {
        id: w.id,
        date: d.date,
        time: to24h(w.timeEt),
        name: w.name,
        period: w.period,
        forecast: formatEconValue(w.forecast, w.unit),
        previous: formatEconValue(w.previous, w.unit),
        actual: formatEconValue(w.actual, w.unit),
        key: w.key,
      } satisfies EconEvent,
    })),
  );
  return { from: p.shownFrom, to: p.shownTo, events: withHistory(rows) };
}

/**
 * The releases of a week (Monday..Sunday of `anchor`'s week) or a month (the
 * Monday on or before the 1st to the Sunday on or after the last) — the
 * server's spans, which always cover the section's weekday views.
 */
export async function fetchEconomic(range: "week" | "month", anchor: string): Promise<EconPayload> {
  if (!econConfigured) throw new Error("No data server configured");
  const params = new URLSearchParams({ range, date: anchor, keyOnly: "false" });
  return mapEconPayload(await get<EconPayloadWire>(`/macro/calendar/econ?${params}`, "Economic calendar"));
}
